import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { setSnackInfo } from "../../redux/actions/appAction";
import HttpService from "../../services/httpService";
import moment from "moment";
import {
    makeStyles,
    Grid,
    Paper,
    TextField,
    Button,
    IconButton,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Avatar,
    Chip,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Skeleton from "@material-ui/lab/Skeleton";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import RefreshIcon from "@material-ui/icons/Refresh";
import PhoneInTalkIcon from "@material-ui/icons/PhoneInTalk";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import TuneIcon from "@material-ui/icons/Tune";
import StarIcon from "@material-ui/icons/Star";

const useStyles = makeStyles((theme) => ({
    page: {
        padding: 24,
        background: "#f4f6fb",
        minHeight: "100%",
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(28,55,93,0.08)",
    },
    header: {
        borderRadius: 16,
        padding: "22px 26px",
        marginBottom: 20,
        background: "linear-gradient(135deg, #1C375D 0%, #2a5ea8 60%, #2a78d6 100%)",
        boxShadow: "0 8px 24px rgba(28,55,93,0.25)",
    },
    title: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontWeight: 700,
        color: "#fff",
        marginBottom: 4,
    },
    subtitle: {
        color: "rgba(255,255,255,0.8)",
    },
    filterPaper: {
        padding: 18,
        borderRadius: 16,
        boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
        marginBottom: 20,
    },
    filterHeader: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 14,
        color: "#1C375D",
        fontWeight: 600,
        fontSize: 15,
    },
    applyButton: {
        borderRadius: 20,
        fontWeight: 600,
        boxShadow: "0 4px 10px rgba(42,120,214,0.32)",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 6px 14px rgba(42,120,214,0.4)",
        },
    },
    resetButton: {
        borderRadius: 20,
        fontWeight: 500,
    },
    refreshButton: {
        borderRadius: 12,
        border: "1px solid rgba(42,120,214,0.3)",
        transition: "transform 0.15s ease, background-color 0.15s ease",
        "&:hover": {
            transform: "rotate(45deg)",
        },
    },
    statCard: {
        position: "relative",
        padding: 16,
        borderRadius: 16,
        display: "flex",
        alignItems: "center",
        gap: 12,
        boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
        height: "100%",
        transition: "transform 0.18s ease, box-shadow 0.18s ease, opacity 0.2s ease",
        "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 10px 24px rgba(28,55,93,0.14)",
        },
    },
    refreshBadge: {
        position: "absolute",
        top: 10,
        right: 10,
    },
    dimmed: {
        opacity: 0.5,
        pointerEvents: "none",
        transition: "opacity 0.2s ease",
    },
    statIcon: {
        width: 44,
        height: 44,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    statValue: {
        fontWeight: 700,
        fontSize: 22,
        lineHeight: 1.1,
    },
    statLabel: {
        color: "#6b7280",
        fontSize: 12,
    },
    accordion: {
        borderRadius: "14px !important",
        marginBottom: 10,
        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        overflow: "hidden",
        transition: "box-shadow 0.15s ease",
        "&:before": { display: "none" },
        "&:hover": {
            boxShadow: "0 6px 18px rgba(28,55,93,0.1)",
        },
    },
    accordionAvailable: {
        borderLeft: "4px solid #1e7e34",
    },
    accordionUnavailable: {
        borderLeft: "4px solid #c62828",
    },
    summaryContent: {
        display: "flex",
        alignItems: "center",
        gap: 14,
        width: "100%",
        flexWrap: "wrap",
    },
    avatarAvailable: {
        backgroundColor: "#e6f4ea",
        color: "#1e7e34",
        fontWeight: 700,
        boxShadow: "0 0 0 3px #e6f4ea",
    },
    avatarUnavailable: {
        backgroundColor: "#fdecea",
        color: "#c62828",
        fontWeight: 700,
        boxShadow: "0 0 0 3px #fdecea",
    },
    userName: {
        fontWeight: 600,
        fontSize: 14,
    },
    userMobile: {
        fontSize: 12,
        color: "#6b7280",
    },
    spacer: {
        flexGrow: 1,
    },
    chipAvailable: {
        backgroundColor: "#e6f4ea",
        color: "#1e7e34",
        fontWeight: 600,
    },
    chipUnavailable: {
        backgroundColor: "#fdecea",
        color: "#c62828",
        fontWeight: 600,
    },
    lastChecked: {
        fontSize: 12,
        color: "#6b7280",
        minWidth: 150,
        textAlign: "right",
    },
    emptyState: {
        textAlign: "center",
        padding: "60px 20px",
        color: "#6b7280",
        borderRadius: 16,
    },
    statusDot: {
        width: 10,
        height: 10,
        borderRadius: "50%",
        display: "inline-block",
        marginRight: 6,
    },
    tableWrapper: {
        borderRadius: 10,
        overflow: "auto",
        maxHeight: 320,
        border: "1px solid #eef0f4",
    },
    refreshingBadge: {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 16px",
        borderRadius: 20,
        background: "#e8f0fe",
        color: "#2a78d6",
        fontSize: 12,
        fontWeight: 600,
        marginBottom: 14,
        boxShadow: "0 2px 8px rgba(42,120,214,0.15)",
        animation: "$pulse 1.4s ease-in-out infinite",
    },
    "@keyframes pulse": {
        "0%": { opacity: 0.7 },
        "50%": { opacity: 1 },
        "100%": { opacity: 0.7 },
    },
    tableHeadCell: {
        position: "sticky",
        top: 0,
        zIndex: 1,
        backgroundColor: "#1C375D",
        color: "#FFF2EB",
        fontWeight: 600,
        whiteSpace: "nowrap",
    },
}));

const HoomanAvailabilityDashboard = (props) => {
    const classes = useStyles();

    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [dateFilter, setDateFilter] = useState({
        startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
    });

    const [visibleCount, setVisibleCount] = useState(300);

    useEffect(() => {
        fetchUsers();
        fetchLogs({ initial: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await HttpService.getUsers({ isActive: true });
            setUsers(response?.data || []);
        } catch (error) {
            console.error("Error fetching users for availability dashboard", error);
        }
    };

    const fetchLogs = async ({ initial = false } = {}) => {
        initial ? setLoading(true) : setRefreshing(true);
        try {
            const params = {
                startDate: dateFilter.startDate,
                endDate: dateFilter.endDate,
                page: 0,
                rowsPerPage: visibleCount,
            };
            if (selectedUser?._id) {
                params.userId = selectedUser._id;
            }
            const response = await HttpService.getAvailabilityLogs(params);
            setLogs(response?.data?.data || []);
            setTotal(response?.data?.total || 0);
        } catch (error) {
            console.error("Error fetching availability logs", error);
            props.setSnackInfo("Failed to load availability logs", "error");
        }
        setLoading(false);
        setRefreshing(false);
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setDateFilter((prev) => ({ ...prev, [name]: value }));
    };

    const handleApply = () => {
        fetchLogs();
    };

    const handleReset = () => {
        setSelectedUser(null);
        setDateFilter({
            startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
            endDate: moment().format("YYYY-MM-DD"),
        });
        setVisibleCount(300);
        setTimeout(() => fetchLogs({ initial: true }), 0);
    };

    const handleLoadMore = () => {
        setVisibleCount((prev) => prev + 300);
        setTimeout(() => fetchLogs(), 0);
    };

    const groupedByUser = useMemo(() => {
        const map = new Map();

        logs.forEach((log) => {
            const key = log.userId?._id || `unmapped-${log.receiverNumber}`;

            if (!map.has(key)) {
                map.set(key, {
                    key,
                    name: log.userId?.name || `Unknown number`,
                    mobile: log.userId?.mobile || log.receiverNumber,
                    checks: [],
                });
            }
            map.get(key).checks.push(log);
        });

        return Array.from(map.values())
            .map((group) => ({
                ...group,
                checks: group.checks.sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                ),
            }))
            .sort(
                (a, b) =>
                    new Date(b.checks[0]?.createdAt) - new Date(a.checks[0]?.createdAt)
            );
    }, [logs]);

    const overallStats = useMemo(() => {
        const availableChecks = logs.filter((l) => l.availabilityFlag).length;
        return {
            totalChecks: logs.length,
            totalUsers: groupedByUser.length,
            availableChecks,
            unavailableChecks: logs.length - availableChecks,
        };
    }, [logs, groupedByUser]);

    const renderSkeletonRows = () =>
        Array.from({ length: 5 }).map((_, i) => (
            <Paper key={i} className={classes.accordion} style={{ padding: 16 }}>
                <Grid container alignItems="center" spacing={2}>
                    <Grid item>
                        <Skeleton variant="circle" width={40} height={40} />
                    </Grid>
                    <Grid item xs>
                        <Skeleton variant="text" width="30%" />
                        <Skeleton variant="text" width="20%" />
                    </Grid>
                    <Grid item>
                        <Skeleton variant="rect" width={80} height={28} style={{ borderRadius: 14 }} />
                    </Grid>
                </Grid>
            </Paper>
        ));

    return (
        <Paper className={classes.page} elevation={0}>
            <div className={classes.header}>
                <Typography variant="h5" className={classes.title}>
                    <StarIcon style={{ color: "#ffd54f" }} />
                    HoomanLabs Availability Dashboard
                </Typography>
                <Typography variant="body2" className={classes.subtitle}>
                    See who's actually picking up availability calls, user by user.
                </Typography>
            </div>

            {/* Filters */}
            <Paper className={classes.filterPaper}>
                <div className={classes.filterHeader}>
                    <TuneIcon fontSize="small" />
                    Filters
                </div>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <Autocomplete
                            options={users}
                            value={selectedUser}
                            onChange={(e, value) => setSelectedUser(value)}
                            getOptionLabel={(option) => option?.name || ""}
                            renderInput={(params) => (
                                <TextField {...params} label="Filter by user" variant="outlined" size="small" />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField
                            name="startDate"
                            label="From"
                            type="date"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={dateFilter.startDate}
                            onChange={handleDateChange}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <TextField
                            name="endDate"
                            label="To"
                            type="date"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={dateFilter.endDate}
                            onChange={handleDateChange}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={2} md={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            className={classes.applyButton}
                            onClick={handleApply}
                            disabled={loading || refreshing}
                        >
                            Apply
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={2} md={2}>
                        <Button
                            variant="outlined"
                            fullWidth
                            className={classes.resetButton}
                            onClick={handleReset}
                            disabled={loading || refreshing}
                        >
                            Reset
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <Tooltip title="Refresh">
                            <span>
                                <IconButton
                                    color="primary"
                                    className={classes.refreshButton}
                                    onClick={() => fetchLogs()}
                                    disabled={loading || refreshing}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Paper>

            {/* Summary stats */}
            <Grid container spacing={2} style={{ marginBottom: 20 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        className={`${classes.statCard} ${refreshing ? classes.dimmed : ""}`}
                        style={{ borderTop: "3px solid #3f51b5" }}
                    >
                        {refreshing && (
                            <div className={classes.refreshBadge}>
                                <CircularProgress size={16} thickness={5} style={{ color: "#3f51b5" }} />
                            </div>
                        )}
                        <div className={classes.statIcon} style={{ background: "#e8eaf6" }}>
                            <PhoneInTalkIcon style={{ color: "#3f51b5" }} />
                        </div>
                        <div>
                            <div className={classes.statValue}>
                                {loading ? <Skeleton width={30} /> : overallStats.totalChecks}
                            </div>
                            <div className={classes.statLabel}>Total Checks</div>
                        </div>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        className={`${classes.statCard} ${refreshing ? classes.dimmed : ""}`}
                        style={{ borderTop: "3px solid #3f51b5" }}
                    >
                        {refreshing && (
                            <div className={classes.refreshBadge}>
                                <CircularProgress size={16} thickness={5} style={{ color: "#3f51b5" }} />
                            </div>
                        )}
                        <div className={classes.statIcon} style={{ background: "#e8eaf6" }}>
                            <PeopleAltIcon style={{ color: "#3f51b5" }} />
                        </div>
                        <div>
                            <div className={classes.statValue}>
                                {loading ? <Skeleton width={30} /> : overallStats.totalUsers}
                            </div>
                            <div className={classes.statLabel}>Users Checked</div>
                        </div>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        className={`${classes.statCard} ${refreshing ? classes.dimmed : ""}`}
                        style={{ borderTop: "3px solid #1e7e34" }}
                    >
                        {refreshing && (
                            <div className={classes.refreshBadge}>
                                <CircularProgress size={16} thickness={5} style={{ color: "#1e7e34" }} />
                            </div>
                        )}
                        <div className={classes.statIcon} style={{ background: "#e6f4ea" }}>
                            <CheckCircleIcon style={{ color: "#1e7e34" }} />
                        </div>
                        <div>
                            <div className={classes.statValue} style={{ color: "#1e7e34" }}>
                                {loading ? <Skeleton width={30} /> : overallStats.availableChecks}
                            </div>
                            <div className={classes.statLabel}>Times Available</div>
                        </div>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper
                        className={`${classes.statCard} ${refreshing ? classes.dimmed : ""}`}
                        style={{ borderTop: "3px solid #c62828" }}
                    >
                        {refreshing && (
                            <div className={classes.refreshBadge}>
                                <CircularProgress size={16} thickness={5} style={{ color: "#c62828" }} />
                            </div>
                        )}
                        <div className={classes.statIcon} style={{ background: "#fdecea" }}>
                            <CancelIcon style={{ color: "#c62828" }} />
                        </div>
                        <div>
                            <div className={classes.statValue} style={{ color: "#c62828" }}>
                                {loading ? <Skeleton width={30} /> : overallStats.unavailableChecks}
                            </div>
                            <div className={classes.statLabel}>Times Unavailable</div>
                        </div>
                    </Paper>
                </Grid>
            </Grid>

            {/* User-wise list */}
            {loading ? (
                renderSkeletonRows()
            ) : groupedByUser.length === 0 ? (
                <Paper className={classes.emptyState}>
                    <AssignmentTurnedInIcon style={{ fontSize: 48, opacity: 0.3 }} />
                    <Typography style={{ marginTop: 10, fontWeight: 500 }}>
                        No availability checks found
                    </Typography>
                    <Typography style={{ fontSize: 13 }}>
                        Try widening the date range or clearing the user filter.
                    </Typography>
                </Paper>
            ) : (
                <>
                    {refreshing && (
                        <div className={classes.refreshingBadge}>
                            <CircularProgress size={14} thickness={5} style={{ color: "#2a78d6" }} />
                            Refreshing latest data...
                        </div>
                    )}

                    <div className={refreshing ? classes.dimmed : undefined}>
                        {groupedByUser.map((group) => {
                            const availableCount = group.checks.filter((c) => c.availabilityFlag).length;
                            const unavailableCount = group.checks.length - availableCount;
                            const latest = group.checks[0];
                            const initials = (group.name || "?")
                                .split(" ")
                                .map((w) => w[0])
                                .slice(0, 2)
                                .join("")
                                .toUpperCase();

                            return (
                                <Accordion
                                    key={group.key}
                                    className={`${classes.accordion} ${latest?.availabilityFlag ? classes.accordionAvailable : classes.accordionUnavailable
                                        }`}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <div className={classes.summaryContent}>
                                            <Avatar
                                                className={
                                                    latest?.availabilityFlag
                                                        ? classes.avatarAvailable
                                                        : classes.avatarUnavailable
                                                }
                                            >
                                                {initials}
                                            </Avatar>
                                            <div>
                                                <div className={classes.userName}>{group.name}</div>
                                                <div className={classes.userMobile}>{group.mobile}</div>
                                            </div>

                                            <div className={classes.spacer} />

                                            <Chip
                                                size="small"
                                                className={classes.chipAvailable}
                                                label={`${availableCount} available`}
                                            />
                                            <Chip
                                                size="small"
                                                className={classes.chipUnavailable}
                                                label={`${unavailableCount} unavailable`}
                                            />

                                            <div className={classes.lastChecked}>
                                                <span
                                                    className={classes.statusDot}
                                                    style={{
                                                        background: latest?.availabilityFlag
                                                            ? "#1e7e34"
                                                            : "#c62828",
                                                    }}
                                                />
                                                Last checked {moment(latest?.createdAt).fromNow()}
                                            </div>
                                        </div>
                                    </AccordionSummary>
                                    <AccordionDetails style={{ display: "block" }}>
                                        <div className={classes.tableWrapper}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell className={classes.tableHeadCell}>Date</TableCell>
                                                        <TableCell className={classes.tableHeadCell}>Time</TableCell>
                                                        <TableCell className={classes.tableHeadCell}>Project</TableCell>
                                                        <TableCell className={classes.tableHeadCell}>Customer</TableCell>
                                                        <TableCell className={classes.tableHeadCell}>Status</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {group.checks.map((check) => (
                                                        <TableRow key={check._id}>
                                                            <TableCell>
                                                                {moment(check.createdAt).format("DD MMM YYYY")}
                                                            </TableCell>
                                                            <TableCell>
                                                                {moment(check.createdAt).format("hh:mm A")}
                                                            </TableCell>
                                                            <TableCell>
                                                                {check.projectId || "—"}
                                                            </TableCell>
                                                            <TableCell>
                                                                {check.customerName || "—"}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    size="small"
                                                                    className={
                                                                        check.availabilityFlag
                                                                            ? classes.chipAvailable
                                                                            : classes.chipUnavailable
                                                                    }
                                                                    label={
                                                                        check.availabilityFlag
                                                                            ? "Available"
                                                                            : "Not available"
                                                                    }
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })}
                    </div>

                    {total > logs.length && (
                        <div style={{ textAlign: "center", marginTop: 16 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                className={classes.resetButton}
                                onClick={handleLoadMore}
                                disabled={refreshing}
                            >
                                Load more ({logs.length} of {total})
                            </Button>
                        </div>
                    )}
                </>
            )}
        </Paper>
    );
};

export default connect(null, { setSnackInfo })(HoomanAvailabilityDashboard);
